import { Text } from "react-native";
import { useResponsive } from "~/hooks/useResponsive";
import { styleConstantsV2 as C } from "~/style/styleConstantsV2";
import type { TextProps, TextStyle } from "react-native";

/**
 * BrandText component for displaying text with specific styles.
 *
 * @param {Object} props - Component props.
 * @param {boolean} sizeH1 - font size is 32px.
 * @param {boolean} sizeH2 - font size is 28px.
 * @param {boolean} sizeH3 - font size is 18px.
 * @param {boolean} sizeH4 - font size is 16px.
 * @param {boolean} sizeH5 - font size is 12px.
 * @param {boolean} weightRegular - font weight is 400.
 * @param {boolean} weightSemiBold - font weight is 600.
 * @param {boolean} weightBold - font weight is 700.
 * @param {boolean} weightExtraBold - font weight is 800.
 */

interface BrandTextProps extends TextProps {
  style?: TextStyle;
  /**  H1=32, H2=28, H3=18, H4=16, H5=12 */
  sizeH1?: boolean;
  /**  H1=32, H2=28, H3=18, H4=16, H5=12 */
  sizeH2?: boolean;
  /**  H1=32, H2=28, H3=18, H4=16, H5=12 */
  sizeH3?: boolean;
  /**  H1=32, H2=28, H3=18, H4=16, H5=12 */
  sizeH4?: boolean;
  /**  H1=32, H2=28, H3=18, H4=16, H5=12 */
  sizeH5?: boolean;
  colorPrimary?: boolean;
  colorBlack?: boolean;
  colorGrey?: boolean;
  colorGrey10?: boolean;
  colorGrey20?: boolean;
  colorGrey30?: boolean;
  colorGrey50?: boolean;

  colorWhite?: boolean;

  // remove colors below when not needed
  colorLink?: boolean;
  /** Regular=400, SemiBold=600, Bold=700, ExtraBold=800 */
  weightRegular?: boolean;
  /** Regular=400, SemiBold=600, Bold=700, ExtraBold=800 */
  weightSemiBold?: boolean;
  /** Regular=400, SemiBold=600, Bold=700, ExtraBold=800 */
  weightBold?: boolean;
  /** Regular=400, SemiBold=600, Bold=700, ExtraBold=800 */
  weightExtraBold?: boolean;
  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  alignJustify?: boolean;

  underline?: boolean;
  lineThrough?: boolean;
}

export const BrandText = ({
  children,
  style: passThroughStyle,
  sizeH1,
  sizeH2,
  sizeH3,
  sizeH4,
  sizeH5,
  colorPrimary,
  colorBlack,
  colorGrey,
  colorGrey10,
  colorGrey20,
  colorGrey30,
  colorGrey50,
  colorLink,
  colorWhite,
  weightRegular,
  weightSemiBold,
  weightBold,
  weightExtraBold,
  alignCenter,
  alignLeft,
  alignRight,
  alignJustify,
  underline,
  lineThrough,
  ...restTextProps
}: BrandTextProps) => {
  const style: TextStyle = {};

  const r = useResponsive();

  style.fontFamily = "AlbertSans-Regular";
  style.fontSize = r(
    C.fontSizeMobileH1,
    C.fontSizeDesktopH1,
    C.fontSizeDesktopH1,
  );
  style.lineHeight = 24;

  if (weightRegular) {
    style.fontFamily = "AlbertSans-Regular";
  } else if (weightSemiBold) {
    style.fontFamily = "AlbertSans-SemiBold";
  } else if (weightBold) {
    style.fontFamily = "AlbertSans-Bold";
  } else if (weightExtraBold) {
    style.fontFamily = "AlbertSans-ExtraBold";
  }

  if (sizeH1) {
    style.fontSize = r(
      C.fontSizeMobileH1,
      C.fontSizeDesktopH1,
      C.fontSizeDesktopH1,
    );
    style.lineHeight = r(36, 40, 40);
  } else if (sizeH2) {
    style.fontSize = r(
      C.fontSizeMobileH2,
      C.fontSizeDesktopH2,
      C.fontSizeDesktopH2,
    );
    style.lineHeight = r(30, 32, 32);
  } else if (sizeH3) {
    style.fontSize = r(
      C.fontSizeMobileH3,
      C.fontSizeDesktopH3,
      C.fontSizeDesktopH3,
    );
    style.lineHeight = r(24, 28, 28);
  } else if (sizeH4) {
    style.fontSize = r(
      C.fontSizeMobileH4,
      C.fontSizeDesktopH4,
      C.fontSizeDesktopH4,
    );
    style.lineHeight = r(18, 24, 24);
  } else if (sizeH5) {
    style.fontSize = r(
      C.fontSizeMobileH5,
      C.fontSizeDesktopH5,
      C.fontSizeDesktopH5,
    );
    style.lineHeight = 16;
  }

  if (colorPrimary) {
    style.color = C.colorPrimary;
  } else if (colorBlack) {
    style.color = C.colorBlack;
  } else if (colorGrey) {
    style.color = C.colorGrey;
  } else if (colorGrey10) {
    style.color = C.colorGrey10;
  } else if (colorGrey20) {
    style.color = C.colorGrey20;
  } else if (colorGrey30) {
    style.color = C.colorGrey30;
  } else if (colorGrey50) {
    style.color = C.colorGrey50;
  } else if (colorWhite) {
    style.color = C.colorWhite;
  } else if (colorLink) {
    style.color = C.colorLink;
  }

  style.textAlign = "auto";
  if (alignCenter) style.textAlign = "center";
  else if (alignLeft) style.textAlign = "left";
  else if (alignRight) style.textAlign = "right";
  else if (alignJustify) style.textAlign = "justify";

  style.textDecorationLine = "none";
  if (underline) style.textDecorationLine = "underline";
  else if (lineThrough) style.textDecorationLine = "line-through";

  return (
    <Text style={[style, passThroughStyle, { ...(restTextProps as any) }]}>
      {children}
    </Text>
  );
};
